import { useEffect, useState } from "react";
import { Download, Share } from "lucide-react";

/**
 * "Install to Home Screen" chip, built specifically for the Companion App.
 *
 *   - Android/Chrome/Edge: shows a purple "Install" button that fires the
 *     native `beforeinstallprompt` flow.
 *   - iOS Safari: browser doesn't expose the prompt event, so we show a
 *     small tappable "Install" chip that opens a tooltip telling the user
 *     to use Share → "Add to Home Screen".
 *   - Already installed / not installable: component returns null.
 *   - User can dismiss via the X; dismissal remembered in localStorage.
 */
const DISMISS_KEY = "glpflo-companion-install-dismissed";

function isStandalone() {
  try {
    if (window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.navigator.standalone) return true; // iOS
  } catch (_) { /* ignore */ }
  return false;
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export default function CompanionInstallChip() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    try { if (localStorage.getItem(DISMISS_KEY) === "1") setDismissed(true); } catch (_) { /* ignore */ }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || dismissed) return null;
  // Only render when we have a prompt OR we're on iOS (manual path).
  const canInstall = !!deferredPrompt;
  const ios = isIOS();
  if (!canInstall && !ios) return null;

  const triggerInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        const choice = await deferredPrompt.userChoice;
        if (choice.outcome === "accepted") setInstalled(true);
      } catch (_) { /* ignore */ }
      setDeferredPrompt(null);
    } else if (ios) {
      setShowIOSHint((v) => !v);
    }
  };

  const dismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (_) { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div className="relative" data-testid="companion-install-chip">
      <div className="flex items-center gap-2 rounded-2xl border border-purple-200 dark:border-purple-900/40 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 px-4 py-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-purple-600 text-white flex items-center justify-center">
          <Download className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-purple-900 dark:text-purple-100 leading-tight">
            Install to home screen
          </p>
          <p className="text-[11px] text-purple-700 dark:text-purple-300 mt-0.5 leading-snug">
            One-tap access — no app store needed.
          </p>
        </div>
        <button
          type="button"
          onClick={triggerInstall}
          className="flex-shrink-0 px-3 py-1.5 rounded-full bg-purple-600 hover:bg-purple-700 active:scale-95 text-white text-xs font-bold transition-all"
          data-testid="companion-install-btn"
        >
          Install
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="flex-shrink-0 text-purple-400 hover:text-purple-700 text-xs font-bold px-1"
          data-testid="companion-install-dismiss"
        >
          ✕
        </button>
      </div>

      {showIOSHint && (
        <div className="mt-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-sm" data-testid="companion-install-ios-hint">
          <p className="font-semibold mb-1 flex items-center gap-1.5"><Share className="w-3.5 h-3.5" /> iOS install</p>
          <ol className="list-decimal list-inside space-y-0.5 text-slate-600 dark:text-slate-300">
            <li>Tap the <b>Share</b> button in Safari&apos;s toolbar</li>
            <li>Scroll and tap <b>&ldquo;Add to Home Screen&rdquo;</b></li>
            <li>Tap <b>Add</b> — the Companion App icon appears on your home screen</li>
          </ol>
        </div>
      )}
    </div>
  );
}
